<script setup>
import { computed, ref, watch } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { required, url } from '@vuelidate/validators';
import { uploadFile } from 'dashboard/helper/uploadHelper';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  template: { type: Object, required: true },
  defaultHeaderImageUrl: { type: String, default: '' },
});

const emit = defineEmits(['update:params', 'save-default-image', 'send-message', 'back']);

// Drag & Drop state
const isDragging = ref(false);
const isUploading = ref(false);
const fileInput = ref(null);

// Parse template components (HEADER, BODY, FOOTER)
const components = computed(() => props.template.components || []);

const headerComponent = computed(() =>
  components.value.find(c => c.type === 'HEADER')
);

const bodyComponent = computed(() =>
  components.value.find(c => c.type === 'BODY')
);

// Header type (TEXT, IMAGE, VIDEO, DOCUMENT)
const headerFormat = computed(() => headerComponent.value?.format?.toUpperCase());
const hasImageHeader = computed(() => headerFormat.value === 'IMAGE');
const hasVideoHeader = computed(() => headerFormat.value === 'VIDEO');
const hasDocumentHeader = computed(() => headerFormat.value === 'DOCUMENT');
const hasMediaHeader = computed(() => hasImageHeader.value || hasVideoHeader.value || hasDocumentHeader.value);

// Extract variables {{1}}, {{2}} etc.
const extractVariables = (text) => {
  if (!text) return [];
  const regex = /{{(\d+)}}/g;
  let match;
  const vars = [];
  while ((match = regex.exec(text)) !== null) {
    vars.push(match[1]);
  }
  return [...new Set(vars)].sort();
};

const variables = computed(() => {
  return extractVariables(bodyComponent.value?.text);
});

const params = ref({});
const headerImageUrl = ref('');
const lastTemplateName = ref('');

// Initialize params - only reset when template NAME actually changes
watch(() => props.template, () => {
  const currentName = props.template?.name || '';

  // Only reset if template name changed (not just object reference)
  if (lastTemplateName.value !== currentName) {
    params.value = {};
    variables.value.forEach(v => {
      params.value[v] = '';
    });
    // Reset header image when template changes
    headerImageUrl.value = props.defaultHeaderImageUrl || '';
    lastTemplateName.value = currentName;
  }
}, { immediate: true });

// Update header image when default changes (always sync after save)
// immediate: true ensures we catch the initial value when page loads
watch(() => props.defaultHeaderImageUrl, (newUrl) => {
  if (newUrl) {
    headerImageUrl.value = newUrl;
  }
}, { immediate: true });

const rules = computed(() => {
  const r = {};
  variables.value.forEach(v => {
    r[v] = { required };
  });
  // Header image required for IMAGE templates
  if (hasImageHeader.value) {
    r.headerImageUrl = { required, url };
  }
  return r;
});

// Combine params and headerImageUrl for validation
const validationState = computed(() => ({
  ...params.value,
  headerImageUrl: headerImageUrl.value,
}));

const v$ = useVuelidate(rules, validationState);

const renderedTemplate = computed(() => {
  let text = bodyComponent.value?.text || '';
  variables.value.forEach(v => {
    const val = params.value[v] || `{{${v}}}`;
    text = text.replace(new RegExp(`{{${v}}}`, 'g'), val);
  });
  return text;
});

const handleSaveDefaultImage = () => {
  if (headerImageUrl.value) {
    emit('save-default-image', {
      templateName: props.template.name,
      imageUrl: headerImageUrl.value,
    });
  }
};

// Drag & Drop handlers
const triggerFileInput = () => {
  if (!isUploading.value && fileInput.value) {
    fileInput.value.click();
  }
};

const handleDrop = (event) => {
  isDragging.value = false;
  const files = event.dataTransfer.files;
  if (files.length > 0) {
    uploadImage(files[0]);
  }
};

const handleFileSelect = (event) => {
  const files = event.target.files;
  if (files.length > 0) {
    uploadImage(files[0]);
  }
  // Reset input so same file can be selected again
  event.target.value = '';
};

const uploadImage = async (file) => {
  // Validate file type
  if (!file.type.startsWith('image/')) {
    useAlert('Lütfen bir görsel dosyası seçin');
    return;
  }
  // Validate file size (max 5MB)
  const maxSize = 5 * 1024 * 1024;
  if (file.size > maxSize) {
    useAlert('Görsel boyutu 5MB\'dan küçük olmalıdır');
    return;
  }

  isUploading.value = true;
  try {
    const { fileUrl } = await uploadFile(file);
    headerImageUrl.value = fileUrl;
    useAlert('Görsel yüklendi');
  } catch (error) {
    useAlert(error.message || 'Görsel yüklenirken hata oluştu');
  } finally {
    isUploading.value = false;
  }
};

const removeImage = () => {
  headerImageUrl.value = '';
};

// Check if form is valid for sending
const isDisabled = computed(() => {
  return v$.value.$invalid;
});

// Send message with all data
const sendMessage = () => {
  if (v$.value.$invalid) return;

  const payload = {
    message: renderedTemplate.value,
    templateParams: {
      name: props.template.name,
      category: props.template.category,
      language: props.template.language,
      namespace: props.template.namespace,
      processed_params: { ...params.value },
    },
  };

  // Add header image URL if template has IMAGE header
  if (hasImageHeader.value && headerImageUrl.value) {
    payload.templateParams.header_image_url = headerImageUrl.value;
  }

  emit('send-message', payload);
};

// Go back to template list
const goBack = () => {
  emit('back');
};

// Expose data to parent
defineExpose({
  v$,
  renderedTemplate,
  processedParams: params,
  headerImageUrl,
  hasMediaHeader,
  sendMessage,
  goBack,
  isDisabled,
});
</script>

<template>
  <div class="flex flex-col gap-4 p-4 border rounded-lg bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-700">
    <h4 class="text-sm font-semibold text-slate-700 dark:text-slate-200">
      👁️ Önizleme
    </h4>

    <!-- Header Image Section -->
    <div v-if="hasImageHeader" class="flex flex-col gap-2">
      <div class="flex items-center gap-2">
        <span class="text-xs font-semibold text-slate-500 uppercase tracking-wider">🖼️ Görsel Header</span>
        <span class="text-xs px-2 py-0.5 rounded" style="background-color: #fef3c7; color: #92400e;">Zorunlu</span>
      </div>

      <!-- Drag & Drop Zone -->
      <div
        class="dropzone"
        :class="{ 'dropzone--active': isDragging, 'dropzone--has-image': headerImageUrl }"
        @dragover.prevent="isDragging = true"
        @dragleave.prevent="isDragging = false"
        @drop.prevent="handleDrop"
        @click="triggerFileInput"
      >
        <input
          ref="fileInput"
          type="file"
          accept="image/*"
          class="hidden"
          @change="handleFileSelect"
        />

        <!-- Upload Progress -->
        <div v-if="isUploading" class="dropzone-content">
          <div class="upload-spinner"></div>
          <span class="text-sm text-slate-500">Yükleniyor...</span>
        </div>

        <!-- Image Preview -->
        <div v-else-if="headerImageUrl" class="dropzone-preview">
          <img
            :src="headerImageUrl"
            alt="Header preview"
            class="max-h-32 rounded object-contain"
            @error="headerImageUrl = ''"
          />
          <button
            type="button"
            class="remove-image-btn"
            @click.stop="removeImage"
          >
            ✕
          </button>
        </div>

        <!-- Empty State -->
        <div v-else class="dropzone-content">
          <span class="text-3xl mb-1">📁</span>
          <span class="text-sm text-slate-500">Görsel sürükle-bırak veya tıkla</span>
          <span class="text-xs text-slate-400">PNG, JPG, JPEG (max 5MB)</span>
        </div>
      </div>

      <!-- URL Input (alternative) -->
      <div class="flex items-center gap-2">
        <span class="text-xs text-slate-400">veya URL girin:</span>
        <input
          v-model="headerImageUrl"
          type="url"
          class="flex-1 px-3 py-1.5 text-sm border rounded outline-none focus:border-woot-500 bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-600"
          placeholder="https://example.com/image.jpg"
          @input="v$.$touch()"
        />
      </div>

      <!-- Actions -->
      <div class="flex items-center justify-between">
        <span v-if="v$.headerImageUrl?.$error" class="text-xs" style="color: #ef4444;">
          Geçerli bir görsel URL'si giriniz
        </span>
        <button
          v-if="headerImageUrl && !v$.headerImageUrl?.$error"
          type="button"
          class="text-xs text-woot-600 hover:underline flex items-center gap-1"
          @click="handleSaveDefaultImage"
        >
          💾 Bu şablon için varsayılan olarak kaydet
        </button>
      </div>
    </div>

    <!-- Video/Document Header Info -->
    <div v-else-if="hasVideoHeader" class="p-3 rounded" style="background-color: #fef3c7; border: 1px solid #fcd34d;">
      <span class="text-sm" style="color: #92400e;">🎥 Bu şablon video header içeriyor - video URL'si gerekli</span>
    </div>
    <div v-else-if="hasDocumentHeader" class="p-3 rounded" style="background-color: #fef3c7; border: 1px solid #fcd34d;">
      <span class="text-sm" style="color: #92400e;">📄 Bu şablon doküman header içeriyor - doküman URL'si gerekli</span>
    </div>

    <!-- Body Preview -->
    <div class="whitespace-pre-wrap text-sm text-slate-600 dark:text-slate-300 bg-white dark:bg-slate-800 p-3 rounded border border-slate-100 dark:border-slate-700">
      {{ renderedTemplate }}
    </div>

    <!-- Body Variables -->
    <div v-if="variables.length > 0" class="flex flex-col gap-3 mt-2">
      <h5 class="text-xs font-semibold text-slate-500 uppercase tracking-wider">
        Değişkenler
      </h5>
      <div v-for="v in variables" :key="v" class="flex flex-col gap-1">
        <label class="text-xs text-slate-600 dark:text-slate-400">
          Değişken {{ v }}
        </label>
        <input
          v-model="params[v]"
          type="text"
          class="w-full px-3 py-2 text-sm border rounded outline-none focus:border-blue-500 bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-600"
          :placeholder="`{{${v}}} değeri`"
          @input="v$.$touch()"
        />
        <span v-if="v$[v]?.$error" class="text-xs" style="color: #ef4444;">
          Bu alan zorunludur
        </span>
      </div>
    </div>

    <!-- Actions slot for custom buttons -->
    <slot
      name="actions"
      :send-message="sendMessage"
      :go-back="goBack"
      :disabled="isDisabled"
    />
  </div>
</template>

<style scoped>
/* Dropzone Styles */
.dropzone {
  @apply relative flex flex-col items-center justify-center p-4 rounded-lg border-2 border-dashed cursor-pointer transition-all;
  @apply border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-900;
  min-height: 120px;
}

.dropzone:hover {
  @apply border-woot-400;
  background-color: rgba(31, 147, 255, 0.05);
}

.dropzone--active {
  @apply border-woot-500;
  background-color: rgba(31, 147, 255, 0.1);
}

.dropzone--has-image {
  @apply p-2;
}

.dropzone-content {
  @apply flex flex-col items-center gap-1;
}

.dropzone-preview {
  @apply relative w-full flex justify-center;
}

.remove-image-btn {
  @apply absolute top-0 right-0 w-6 h-6 flex items-center justify-center rounded-full text-xs;
  @apply bg-slate-700 text-white;
}

.remove-image-btn:hover {
  background-color: #ef4444;
}

.upload-spinner {
  @apply w-6 h-6 border-2 border-woot-500 rounded-full;
  border-top-color: transparent;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.hidden {
  display: none;
}
</style>
